import api from './api';
import {
  Festivity,
  HolidayWithFestivity,
  CalendarHoliday,
  HolidayCountry,
  AddHolidaysData,
  HolidayFilters,
  UpdateFestivityData,
  HolidayApiResponse,
} from '@/types/holidays';

/**
 * Holidays API Service
 *
 * API client for holiday configuration and management
 */

export const holidaysService = {
  /**
   * Add holiday configuration to calendar
   * Imports holidays in background from Nager.Date API
   */
  addHolidays: async (data: AddHolidaysData): Promise<Festivity> => {
    const { data: response } = await api.post<HolidayApiResponse<Festivity>>(
      '/api/holidays',
      data
    );
    return response.data;
  },

  /**
   * Get holidays for calendar view (specific month)
   * Returns holidays with festivity color/country info
   */
  getCalendarViewHolidays: async (filters: HolidayFilters): Promise<HolidayWithFestivity[]> => {
    const queryParams = new URLSearchParams({
      calendarId: filters.calendarId.toString(),
      month: filters.month,
    });

    const { data } = await api.get<HolidayApiResponse<HolidayWithFestivity[]>>(
      `/api/holidays/calendar-view?${queryParams.toString()}`
    );
    return data.data;
  },

  /**
   * Update holiday display settings (color, status)
   */
  updateFestivity: async (
    festivityId: number,
    data: UpdateFestivityData
  ): Promise<Festivity> => {
    const { data: response } = await api.patch<HolidayApiResponse<Festivity>>(
      `/api/holidays/${festivityId}`,
      data
    );
    return response.data;
  },

  /**
   * Remove holiday configuration from calendar
   */
  deleteFestivity: async (festivityId: number): Promise<void> => {
    await api.delete(`/api/holidays/${festivityId}`);
  },

  /**
   * Re-fetch holidays from Nager.Date API
   * Useful to get updated holiday data
   */
  refreshHolidays: async (festivityId: number): Promise<{ count: number }> => {
    const { data } = await api.post<HolidayApiResponse<{ count: number }>>(
      `/api/holidays/${festivityId}/refresh`
    );
    return data.data;
  },

  /**
   * Get list of available countries
   */
  getCountries: async (): Promise<HolidayCountry[]> => {
    const { data } = await api.get<HolidayApiResponse<HolidayCountry[]>>(
      '/api/holidays/countries'
    );
    return data.data;
  },

  /**
   * Get holidays for calendar in date range
   * Used by calendar grid to display holidays
   */
  getHolidaysForCalendar: async (
    calendarId: number,
    startDate: string,
    endDate: string
  ): Promise<CalendarHoliday[]> => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await api.get<HolidayApiResponse<CalendarHoliday[]>>(
      `/api/holidays/calendar/${calendarId}?${params.toString()}`
    );
    return response.data.data;
  },
};
